/**
 * CRA3D Paywall — customer-facing plans / upgrade page.
 *
 * 2026-02 pricing consolidation (Mark's authorised scope):
 *   - Two clear launch offers:
 *       (a) Quick Repath (FREE) — 18-question path + preliminary snapshot
 *           + downloadable snapshot PDF. No card required.
 *       (b) Complete Career Repath Assessment ($24.99 one-time) — full
 *           56-question path + profile + three evidence-linked Lanes of
 *           Direction + full PDF + JSON export + initial action-plan.
 *   - Annual AI Training ($149.99 / 12 mo) is displayed as a "coming
 *     later" note with NO purchase CTA and NO fake availability copy.
 *   - Resume Studio card is hidden while RESUME_STUDIO_ENABLED is off
 *     (backend already filters).
 *   - The `full_cra3d_experience` recurring row is hidden while
 *     ANNUAL_TRAINING_ENABLED is off (backend already filters).
 *
 * Preserved contract:
 *   - Server derives every price via /api/paywall/checkout — the
 *     frontend never sends amounts.
 *   - Signed-out purchasers are redirected to /register with the
 *     intent slug preserved so the flow resumes post-auth.
 *   - "Currently active on your account" badge still lights when the
 *     current user already holds the product grant.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, XCircle, Sparkles, ShieldCheck, Loader2, TestTube2,
  ArrowRight, GraduationCap, FileText, Lock,
} from "lucide-react";
import { toast } from "sonner";


// Human-readable labels for capability keys (used by both cards).
const HUMAN_CAP_LABELS = {
  assessment_access:                   "Career Repath Assessment",
  complete_assessment_access:          "Complete 56-question assessment",
  reconsiderations_cap:                "Direction reconsiderations",
  assessment_pdf_download_enabled:     "Full assessment PDF download",
  assessment_json_export_enabled:      "JSON export of the full analysis (directions + evidence + profile)",
  initial_action_plan_access:          "Initial action-plan outline",
  resume_studio_access:                "Résumé Studio (preview + editing)",
  resume_studio_ai_draft:              "AI-drafted résumé claims",
  resume_studio_ai_draft_daily_cap:    "AI drafts per day",
  resume_studio_exports:               "Résumé downloads",
  resume_studio_saved_resumes_cap:     "Saved résumés",
  voice_transcription_enabled:         "Voice input on every free-text field",
  voice_transcription_daily_minutes_cap: "Voice input minutes per day",
  foundations_access:                  "Foundations learning pathway",
  pathway_access:                      "Additional learning pathways",
};

// Capability keys that are legacy or internal duplicates and must NOT
// render on customer-facing cards (F3, 2026-02). `pdf_exports_enabled`
// is a legacy alias for `assessment_pdf_download_enabled` on the same
// paid product — one bullet is enough.
const HIDDEN_CAP_KEYS = new Set([
  "pdf_exports_enabled",
]);


function capBullet(k, v) {
  const label = HUMAN_CAP_LABELS[k] || k;
  if (typeof v === "boolean") return { label, included: v };
  if (v === "none") return { label, included: false };
  if (v === "unlimited") return { label, included: true, note: "Unlimited" };
  if (v === "one_resume") return { label, included: true, note: "One résumé" };
  if (v === "direction_bundle") return { label, included: true, note: "Up to 3 (one per Lane of Direction)" };
  if (v === "all") return { label, included: true, note: "All published" };
  if (typeof v === "number") {
    if (v === -1) return { label, included: true, note: "Unlimited" };
    if (v === 0) return { label, included: false };
    if (k === "reconsiderations_cap") {
      return {
        label: `${v} direction reconsiderations included`,
        included: true,
        note: "lifetime allowance, not a daily limit",
      };
    }
    return { label, included: true, note: `Up to ${v} / day` };
  }
  return { label, included: !!v };
}


function formatUSD(cents) {
  if (cents == null) return null;
  if (cents === 0) return "$0";
  return `$${(cents / 100).toFixed(2)}`;
}


export default function Plans() {
  const [params] = useSearchParams();
  const gatedFrom = params.get("gated_from");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({
    products: [], payments_active: false,
    payments_mode: "disabled", test_mode: false,
    annual_training_enabled: false,
    training_pricing_enabled: false,
  });
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null);   // slug key

  // Purchase-intent handoff — when the user lands on /plans with
  // ?intent_slug=X after registration/login, auto-invoke checkout so
  // buyers arriving from the Landing hero don't have to click twice.
  // Only fires for signed-in users with a valid PAID slug and no
  // active grant. If any of those aren't true, we leave the page
  // untouched (Plans handles all three states gracefully).
  const intentSlug = params.get("intent_slug");
  const [intentConsumed, setIntentConsumed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (user) {
          const [pl, acc] = await Promise.all([
            api.get("/plans"),
            api.get("/me/access"),
          ]);
          setState(pl.data);
          setAccess(acc.data);
        } else {
          const pl = await api.get("/plans");
          setState(pl.data);
          setAccess(null);
        }
      } catch (err) {
        setError(extractApiError(err, "Couldn't load plans right now."));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const activeGrantSlugs = useMemo(
    () => new Set((access?.active_grants || []).map((g) => g.product_slug)),
    [access],
  );

  const paymentsMode = state.payments_mode || "disabled";
  const paymentsOpen = paymentsMode !== "disabled";
  const testMode = paymentsMode === "test";
  const annualTrainingOn = !!state.annual_training_enabled;
  const trainingPricingOn = !!state.training_pricing_enabled;
  const resumeStudioOn = !!state.resume_studio_enabled;

  // Split the returned products into the two customer packages so
  // rendering is explicit and copy is authoritative — never inferred
  // by slug order.
  const freeProduct = state.products.find((p) => p.slug === "free_explorer");
  const completeProduct = state.products.find((p) => p.slug === "three_directions");

  // 2026-10 AI Training products (visible only when flag is on).
  const trainingProducts = useMemo(
    () => Object.fromEntries(
      state.products
        .filter((p) => p.slug.startsWith("training_"))
        .map((p) => [p.slug, p]),
    ),
    [state.products],
  );

  async function handleBuy(slug, interval = null) {
    if (!user) {
      const qs = new URLSearchParams({
        redirect: "/plans",
        intent_slug: slug,
      });
      navigate(`/register?${qs.toString()}`);
      return;
    }
    setCheckoutBusy(slug);
    try {
      const res = await api.post("/paywall/checkout", {
        slug, interval,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.assign(res.data.checkout_url);
      } else {
        toast.error("Couldn't start checkout — no session URL returned.");
        setCheckoutBusy(null);
      }
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't start checkout."));
      setCheckoutBusy(null);
    }
  }

  const completeCents = (completeProduct?.draft_prices?.one_time || {}).USD;

  // Consume purchase intent AFTER products + access are loaded. Guards:
  //   - user is signed in (unauth clicks route to /register first)
  //   - intent slug refers to a PAID product currently in the catalog
  //   - user does NOT already hold an active grant for it (no
  //     duplicate-purchase prompts, per spec)
  //   - not already busy on another checkout
  useEffect(() => {
    if (intentConsumed) return;
    if (!user || !intentSlug) return;
    if (loading) return;
    const product = state.products.find((p) => p.slug === intentSlug);
    if (!product) return;
    const isPaid = ((product.draft_prices?.one_time || {}).USD || 0) > 0
                 || Object.keys(product.draft_prices || {}).some((k) => k !== "one_time");
    if (!isPaid) return;
    if (activeGrantSlugs.has(intentSlug)) return;
    if (!paymentsOpen) return;
    if (checkoutBusy) return;
    setIntentConsumed(true);
    handleBuy(intentSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentSlug, user, loading, state.products, activeGrantSlugs, paymentsOpen, checkoutBusy, intentConsumed]);

  return (
    <AppShell>
      {/* H1 (R29): visible overlay between the Buy click and the browser
          unload to Stripe's hosted checkout. Prevents the "white screen"
          perception reported during the owner's manual test by giving
          unambiguous visual feedback that the redirect is in flight.
          Pure UX; no business logic change; does not disable existing
          toast / error paths. */}
      {checkoutBusy && (
        <div
          data-testid="buy-redirect-overlay"
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-bg-base/80 backdrop-blur-sm"
        >
          <div className="mx-4 max-w-md rounded-lg border border-border-subtle bg-bg-surface px-6 py-5 shadow-lg">
            <div className="flex items-start gap-4">
              <Loader2 className="mt-0.5 h-6 w-6 flex-shrink-0 animate-spin text-accent-warm" aria-hidden="true" />
              <div className="min-w-0">
                <p
                  data-testid="buy-redirect-overlay-title"
                  className="text-base font-heading font-medium text-fg-primary"
                >
                  Taking you to Stripe&rsquo;s secure payment page&hellip;
                </p>
                <p
                  data-testid="buy-redirect-overlay-body"
                  className="mt-1 text-xs text-fg-secondary leading-relaxed"
                >
                  Your card is entered on Stripe, not on CRA3D. If this screen
                  stays visible for more than 10&nbsp;seconds, your browser or
                  an extension may be blocking Stripe scripts &mdash; try
                  disabling content-blockers for{" "}
                  <code className="rounded bg-bg-elevated px-1 py-0.5">checkout.stripe.com</code>{" "}
                  or open the site in a private window.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        data-testid="plans-page"
        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div className="mb-8">
          <p className="text-xs uppercase tracking-wider text-fg-secondary">
            Access
          </p>
          <h1
            data-testid="plans-title"
            className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-fg-primary leading-tight"
          >
            Your career isn&rsquo;t a one-time decision.
            <span className="block text-fg-secondary">It&rsquo;s a path you can revisit.</span>
          </h1>
          <p
            data-testid="plans-subtitle"
            className="mt-4 max-w-2xl text-base text-fg-secondary leading-relaxed"
          >
            Two clear ways to begin. Start free with the Quick Repath and a
            preliminary snapshot, or unlock the Complete Career Repath
            Assessment for a one-time $24.99 &mdash; full 56 questions,
            three evidence-linked Lanes of Direction, a downloadable PDF
            report, JSON export, and an initial action-plan outline.
          </p>
        </div>

        {testMode && (
          <Alert
            data-testid="plans-test-mode-banner"
            className="mb-6 border-amber-500/50 bg-amber-500/10 text-fg-primary"
          >
            <TestTube2 className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              <strong>Test mode.</strong> Checkout runs against Stripe&rsquo;s sandbox.
              No real card is charged. Use test card{" "}
              <code className="rounded bg-bg-elevated px-1.5 py-0.5 break-all">4242 4242 4242 4242</code>,
              any future expiry, and any 3-digit CVC.
            </AlertDescription>
          </Alert>
        )}

        {gatedFrom && (
          <Alert
            data-testid="plans-gated-alert"
            className="mb-6 border-amber-500/30 bg-amber-500/5"
          >
            <AlertDescription className="text-fg-primary">
              You landed here because <strong>{gatedFrom}</strong>{" "}
              needs the Complete Career Repath Assessment.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" data-testid="plans-error" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Round-27 — stable loading state. Pre-R27 we swapped the
            entire grid for a "Loading plans…" placeholder, which
            caused the owner-observed card flash where pricing cards
            appeared → disappeared → reappeared during account-data
            refresh. We now keep the full plans grid in the DOM
            even while /plans + /me/access are in flight; the only
            thing that changes between the loading and settled
            states is the per-card CTA button (skeletons until data
            arrives, real buttons after). The `plans-loading` test
            id is preserved as a transient badge on the grid for
            instrumentation but does NOT replace the grid itself. */}
        {loading && (
          <p
            className="text-xs text-fg-secondary text-center py-2"
            data-testid="plans-loading"
          >
            Loading your account state&hellip; pricing is current and
            visible below.
          </p>
        )}
        {!error && (
          <>
            <div className="grid gap-6 md:grid-cols-2" data-testid="plans-grid">
              {/* Quick Repath — Free */}
              {freeProduct && (
                <div
                  data-testid="plans-card-free_explorer"
                  className="relative rounded-lg border border-border-subtle bg-bg-surface p-6 flex flex-col"
                >
                  <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
                    Free &middot; No card required
                  </p>
                  <h2
                    data-testid="plans-card-free_explorer-name"
                    className="mt-1 text-xl font-heading text-fg-primary"
                  >
                    Quick Repath
                  </h2>
                  <p
                    data-testid="plans-card-free_explorer-price"
                    className="mt-3 text-3xl font-heading text-fg-primary leading-tight"
                  >
                    $0
                    <span className="text-sm text-fg-secondary ml-2">forever</span>
                  </p>
                  <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
                    An 18-question path across the same six areas as the
                    complete assessment. You&rsquo;ll receive a preliminary
                    snapshot of interests, strengths, and possible direction
                    areas &mdash; grounded in your own words, with every
                    conclusion editable or rejectable.
                  </p>
                  <ul
                    data-testid="plans-card-free_explorer-inclusions"
                    className="mt-5 space-y-1.5 text-sm text-fg-primary flex-1"
                  >
                    <li className="flex items-start gap-2" data-testid="free-incl-quick">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>18-question Quick Repath assessment</span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="free-incl-snapshot">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>Preliminary Quick Repath Snapshot (AI-generated, evidence-linked)</span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="free-incl-snapshot-pdf">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>Downloadable preliminary snapshot PDF</span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="free-incl-account-data-json">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>Account-data JSON export (Settings &rarr; Export &mdash; your own answers + consents)</span>
                    </li>
                    <li className="flex items-start gap-2" data-testid="free-incl-carryover">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>Answers carry over automatically if you upgrade</span>
                    </li>
                    <li className="flex items-start gap-2 text-fg-muted" data-testid="free-excl-directions">
                      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>Not included: full 56-question assessment, three Lanes of Direction, full report PDF, full-analysis JSON export, action-plan outline</span>
                    </li>
                  </ul>
                  <div className="mt-6">
                    <Button
                      onClick={() => user ? navigate("/assessment/choose") : navigate("/register?intent_slug=free_explorer")}
                      data-testid="plans-cta-free-quick"
                      className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                    >
                      Start free &mdash; 18 questions
                      <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Complete Career Repath Assessment — $24.99 one-time */}
              {completeProduct && (
                <div
                  data-testid="plans-card-three_directions"
                  className="relative rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-6 flex flex-col shadow-md"
                >
                  <Badge
                    data-testid="plans-card-three_directions-featured-badge"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest px-3"
                  >
                    Complete Assessment
                  </Badge>
                  <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
                    One-time &middot; No recurring charge
                  </p>
                  <h2
                    data-testid="plans-card-three_directions-name"
                    className="mt-1 text-xl font-heading text-fg-primary"
                  >
                    {completeProduct.name}
                  </h2>
                  {paymentsOpen ? (
                    <p
                      data-testid="plans-card-three_directions-price"
                      className="mt-3 text-3xl font-heading text-fg-primary leading-tight"
                    >
                      {formatUSD(completeCents)}
                      <span className="text-sm text-fg-secondary ml-2">one-time</span>
                    </p>
                  ) : (
                    <p className="mt-3 text-xs italic text-fg-muted">
                      Pricing announced when purchasing opens.
                    </p>
                  )}
                  <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
                    {completeProduct.description}
                  </p>
                  <ul
                    data-testid="plans-card-three_directions-inclusions"
                    className="mt-5 space-y-1.5 text-sm text-fg-primary flex-1"
                  >
                    {Object.entries(completeProduct.capabilities || {}).map(([k, v]) => {
                      if (HIDDEN_CAP_KEYS.has(k)) return null;
                      const b = capBullet(k, v);
                      if (!b || !b.label) return null;
                      return (
                        <li
                          key={k}
                          data-testid={`plans-card-three_directions-cap-${k}`}
                          className="flex items-start gap-2"
                        >
                          {b.included ? (
                            <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                          ) : (
                            <XCircle className="h-4 w-4 text-fg-muted flex-shrink-0 mt-0.5" aria-hidden="true" />
                          )}
                          <span>
                            {b.label}
                            {b.note && (
                              <span className="text-fg-muted"> &mdash; {b.note}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                    <li className="flex items-start gap-2 text-fg-secondary" data-testid="complete-note-no-recurring">
                      <CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>No recurring assessment charge. AI training is a separate offer that isn&rsquo;t available yet.</span>
                    </li>
                  </ul>
                  <div className="mt-6">
                    {activeGrantSlugs.has("three_directions") ? (
                      <p
                        data-testid="plans-card-three_directions-granted-badge"
                        className="text-[12px] uppercase tracking-widest text-progress-teal flex items-center gap-1"
                      >
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        Currently active on your account
                      </p>
                    ) : paymentsOpen ? (
                      <Button
                        data-testid="plans-card-three_directions-buy-btn"
                        onClick={() => handleBuy("three_directions")}
                        disabled={checkoutBusy != null}
                        className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                      >
                        {checkoutBusy === "three_directions" ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting&hellip;</>
                        ) : (
                          <>Unlock complete assessment &mdash; {formatUSD(completeCents)}</>
                        )}
                      </Button>
                    ) : (
                      <p className="text-xs italic text-fg-muted">
                        Purchasing not open yet.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 2026-10 AI Training section — visible only when
                training_pricing_enabled=true. All amounts come from
                the server; the Buy buttons route to /paywall/checkout
                which cross-checks Stripe prices + verifies upgrade
                ownership. */}
            {trainingPricingOn && (
              <TrainingSection
                products={trainingProducts}
                paymentsOpen={paymentsOpen}
                activeGrantSlugs={activeGrantSlugs}
                checkoutBusy={checkoutBusy}
                onBuy={handleBuy}
                signedIn={!!user}
              />
            )}

            {/* Coming later — Annual AI Training (non-purchasable disclosure) */}
            {!annualTrainingOn && !trainingPricingOn && (
              <div
                data-testid="plans-annual-training-coming-later"
                className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6"
              >
                <p className="text-xs uppercase tracking-wider text-fg-secondary">Coming later</p>
                <h3 className="mt-1 text-lg font-heading text-fg-primary">
                  Guided AI Learning &mdash; extended access
                </h3>
                <p className="mt-2 text-sm text-fg-secondary leading-relaxed">
                  Direction-specific AI learning bundles are being designed.
                  Planned access: 30 days $29.99, 60 days $49.99, 90 days $69.99,
                  or an annual membership at $149.99 billed annually for the
                  first year&mdash;about $12.50/month equivalent. The annual
                  option renews automatically at $169.99 per year. The
                  Complete Career Repath Assessment ($24.99 one-time) is a
                  separate offer and is not bundled into the training
                  packages above.{" "}
                  <span className="text-fg-muted italic">
                    Nothing here is purchasable yet.
                  </span>{" "}
                  Join the waitlist on the{" "}
                  <Link
                    to="/#guided-ai-learning"
                    className="underline hover:text-fg-primary"
                    data-testid="plans-annual-training-waitlist-link"
                  >
                    landing page
                  </Link>{" "}
                  to be notified when it launches.
                </p>
              </div>
            )}

            {/* 6c — Resume Studio Single limits disclosure (text
                only; Mark approved 2026-10-06 after R6 policy
                sign-off, PREVIEW ONLY). Lists the verified existing
                caps from services/products/catalog.py — no pricing,
                entitlement, cap, or feature changes. Hidden when
                Resume Studio is disabled site-wide. */}
            {resumeStudioOn && (
              <div
                data-testid="plans-resume-studio-single-limits-tile"
                className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6"
              >
                <p className="text-xs uppercase tracking-wider text-fg-secondary">
                  Resume Studio Single &middot; verified limits
                </p>
                <h3 className="mt-1 text-lg font-heading text-fg-primary">
                  What a Resume Studio Single purchase includes
                </h3>
                <p className="mt-2 text-sm text-fg-secondary leading-relaxed">
                  Factual summary of the caps that already apply on
                  this tier. These numbers come from the live product
                  catalog and are not changed by this disclosure.
                </p>
                <ul
                  data-testid="plans-resume-studio-single-limits-list"
                  className="mt-3 text-sm text-fg-secondary leading-relaxed list-disc pl-5 space-y-1"
                >
                  <li>
                    <strong className="text-fg-primary">
                      1 saved resume
                    </strong>{" "}
                    on your account.
                  </li>
                  <li>
                    <strong className="text-fg-primary">
                      25 AI-draft calls per day
                    </strong>{" "}
                    for AI-assisted drafting.
                  </li>
                  <li>
                    <strong className="text-fg-primary">
                      5 uploads per day
                    </strong>{" "}
                    (PDF or DOCX resumes).
                  </li>
                  <li>
                    <strong className="text-fg-primary">
                      Supported exports: PDF, DOCX, and TXT
                    </strong>{" "}
                    &mdash; ATS-safe downloads of your approved
                    resume.
                  </li>
                </ul>
              </div>
            )}

            {/* Trust + policy footer */}
            <div
              data-testid="plans-trust-footer"
              className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6"
            >
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div className="text-sm text-fg-primary space-y-2">
                  <p>
                    Payments are processed by <strong>Stripe</strong> using
                    hosted checkout &mdash; we never see or store your card
                    number. All prices are in <strong>USD</strong>. CRA3D
                    currently sells to U.S. customers only.
                  </p>
                  <p className="text-fg-secondary">
                    <strong>Refunds &mdash; Complete Career Repath
                    Assessment:</strong> fully refundable within 7 days
                    if the report hasn&rsquo;t been used.
                  </p>
                  <p
                    data-testid="plans-trust-footer-training-refund"
                    className="text-fg-secondary"
                  >
                    <strong>Refunds &mdash; AI Training:</strong> every
                    refund window is exactly 168 hours from the charge
                    timestamp when paid access is available, or from
                    actual activation if access is delayed &mdash;
                    measured in UTC. One-time packages (30/60/90 days):
                    12 months of access from purchase; satisfaction
                    refunds within exactly 168 hours while fewer than
                    three required paid lessons are explicitly marked
                    complete (Start Here and free sample lessons do
                    not count toward the three).{" "}
                    <strong>Annual membership is not yet on sale.</strong>
                  </p>
                  <p className="text-fg-secondary">
                    Billing errors, duplicate charges,
                    service-delivery problems, undelivered content,
                    and content-availability complaints are reviewed
                    on their own merits regardless of the window.
                    Full details in our{" "}
                    <Link
                      to="/refund-policy"
                      data-testid="plans-refund-policy-link"
                      className="underline hover:text-fg-primary"
                    >
                      Refund &amp; Cancellation Policy
                    </Link>.
                  </p>
                  <p data-testid="plans-support-contact-note" className="text-fg-secondary">
                    Questions or refund requests? Email{" "}
                    <a
                      href="mailto:support@cra3d.com"
                      data-testid="plans-support-contact-mailto"
                      className="underline hover:text-fg-primary"
                    >
                      support@cra3d.com
                    </a>
                    .
                  </p>
                  <p className="text-fg-secondary">
                    By purchasing you agree to our{" "}
                    <Link to="/terms" className="underline hover:text-fg-primary"
                          data-testid="plans-terms-link">Terms</Link>{" "}and{" "}
                    <Link to="/privacy" className="underline hover:text-fg-primary"
                          data-testid="plans-privacy-link">Privacy Policy</Link>.
                    No guarantees of employment, income, or interviews.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}


/**
 * AI Training catalog section (preview-only; gated by
 * `training_pricing_enabled`). Renders:
 *   - Draft-policy disclosure banner
 *   - Three package cards (30/60/90-day) with Buy buttons
 *   - Contextual upgrade credit cards (shown only when the predecessor
 *     grant is active on the user's account — otherwise the backend
 *     refuses the $20.00 checkout anyway)
 *   - Two membership cards (Monthly $24.99/mo, Annual $149.99 first
 *     year renewing at $169.99/yr via a real Stripe SubscriptionSchedule)
 */
function TrainingSection({ products, paymentsOpen, activeGrantSlugs,
                           checkoutBusy, onBuy, signedIn }) {
  const pkg30 = products.training_30d;
  const pkg60 = products.training_60d;
  const pkg90 = products.training_90d;
  const up3060 = products.training_30_to_60_upgrade;
  const up6090 = products.training_60_to_90_upgrade;
  // Monthly membership removed 2026-10-05 (pricing correction). The
  // $24.99 figure is the EXISTING one-time Complete Career Repath
  // Assessment price, not a training subscription — a monthly card
  // here was a mistake. If a stale row reappears, nothing renders.
  const annual  = products.training_membership_annual;

  const owns30 = activeGrantSlugs.has("training_30d");
  const owns60 = activeGrantSlugs.has("training_60d");
  const owns90 = activeGrantSlugs.has("training_90d");
  const ownsAnnual  = activeGrantSlugs.has("training_membership_annual");

  const annualPhases = annual?.pricing_phases || [];

  return (
    <section
      data-testid="plans-training-section"
      className="mt-10 pt-10 border-t border-border-subtle"
    >
      <div className="flex items-start gap-3 mb-4">
        <GraduationCap
          className="h-6 w-6 text-accent-warm flex-shrink-0 mt-1"
          aria-hidden="true"
        />
        <div>
          <p className="text-xs uppercase tracking-wider text-fg-secondary">
            Preview &middot; AI Training
          </p>
          <h2
            data-testid="plans-training-title"
            className="mt-1 text-2xl sm:text-3xl font-heading font-medium text-fg-primary leading-tight"
          >
            Practical AI skill training
          </h2>
          <p
            data-testid="plans-training-subtitle"
            className="mt-2 max-w-2xl text-sm text-fg-secondary leading-relaxed"
          >
            One free sample lesson is available today; Level 1, 2, and
            3 are in production and not yet published. Package names
            describe a suggested learning schedule &mdash; nothing is
            time-locked. See the honest status for every lesson on the{" "}
            <Link
              to="/training"
              data-testid="plans-training-page-link"
              className="underline hover:text-fg-primary"
            >
              AI Training page
            </Link>
            .
          </p>
        </div>
      </div>

      <Alert
        data-testid="plans-training-refund-banner"
        className="mb-6 border-progress-teal/40 bg-progress-teal/5 text-fg-primary"
      >
        <FileText className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          <strong>AI Training refund terms.</strong>{" "}
          Mark&rsquo;s approved cancellation and refund terms for AI
          Training are published alongside the existing CRA3D refund
          policy.{" "}
          <Link
            to="/refund-policy"
            data-testid="plans-training-refund-policy-link"
            className="underline hover:text-fg-primary"
          >
            Read the Refund &amp; Cancellation Policy
          </Link>
          .
        </AlertDescription>
      </Alert>

      {/* Round-15: Annual-membership row rendered FIRST (ABOVE the
          one-time tiles) as the prominent, recommended offer. When
          TRAINING_ANNUAL_ENABLED is off the backend filters the
          product out of /api/plans and the "not yet on sale"
          honest fallback renders instead. Production stays untouched
          (flag unset -> False). No countdowns, no urgency; just
          placement + a Recommended badge + a larger card. */}
      <div
        data-testid="plans-training-memberships"
        className="mb-10"
      >
        {annual ? (
          <AnnualMembershipCard
            product={annual}
            phases={annualPhases}
            alreadyOwned={ownsAnnual}
            paymentsOpen={paymentsOpen}
            checkoutBusy={checkoutBusy}
            onBuy={() => onBuy("training_membership_annual", "annual")}
            testid="plans-training-annual"
            signedIn={signedIn}
          />
        ) : (
          <div
            data-testid="plans-training-annual-not-yet-on-sale"
            className="rounded-lg border border-dashed border-border-subtle bg-bg-surface p-5 text-sm text-fg-secondary leading-relaxed md:max-w-xl md:mx-auto"
          >
            <p className="font-medium text-fg-primary mb-1">
              Annual AI Training membership &mdash; not yet on sale
            </p>
            <p>
              The annual AI Training membership isn&rsquo;t available
              for purchase yet. Members who have already subscribed
              keep their 12-month program timeline intact.{" "}
              <Link
                to="/refund-policy"
                data-testid="plans-training-annual-refund-policy-link"
                className="underline hover:text-fg-primary"
              >
                Read the Refund &amp; Cancellation Policy
              </Link>{" "}
              for the terms that will apply when it opens.
            </p>
          </div>
        )}
      </div>

      {/* Divider + "Prefer a one-time package?" label so the three
          tiles are a clear secondary offer rather than competing with
          the annual card. */}
      <div
        data-testid="plans-training-onetime-header"
        className="mb-4 pt-6 border-t border-border-subtle"
      >
        <h3 className="text-sm font-heading font-medium text-fg-primary uppercase tracking-widest">
          Prefer a one-time package?
        </h3>
        <p className="mt-1 text-xs text-fg-secondary leading-relaxed max-w-2xl">
          One-time training packages let you buy a defined scope
          outright. Package names describe a suggested learning pace;
          access lasts 12 months from purchase regardless of the named
          number of days.
        </p>
      </div>

      {/* One-time package disclosure (approved §B.1 wording,
          Round-9 2026-10-06). Rendered once near the package
          cards so a buyer sees the refund window + cutoff
          mechanics at the point of purchase. */}
      <div
        data-testid="plans-training-onetime-disclosure"
        className="mb-6 rounded-lg border border-border-subtle bg-bg-surface p-5 text-sm text-fg-secondary leading-relaxed"
      >
        <p className="font-medium text-fg-primary mb-2">
          Refund policy &mdash; one-time training packages
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-fg-primary">Access:</strong>{" "}
            12 months from purchase. Package names (30/60/90 days)
            describe a suggested learning pace; access does not lock
            mid-term merely because the named number of days passed.
          </li>
          <li>
            <strong className="text-fg-primary">Satisfaction
            refund:</strong>{" "}
            available within exactly 168 hours from the charge
            timestamp when paid access is available, or from actual
            activation if access is delayed &mdash; measured in UTC
            &mdash; while fewer than three required paid lessons have
            been explicitly marked complete. Start Here and free
            sample lessons do not count toward the three. Viewing or
            downloading content does not count &mdash; only an
            explicit &ldquo;Mark complete&rdquo;.
          </li>
          <li>
            Outside the 168-hour window, or once the third required
            paid lesson has been explicitly marked complete, routine
            refunds are not issued.
          </li>
          <li>
            Billing errors, service-delivery problems, undelivered
            content, duplicate charges, and content-availability
            complaints are reviewed on their own merits regardless of
            the window or the cutoff.
          </li>
          <li>
            The upgrades between packages (30&rarr;60 and 60&rarr;90)
            are additive; refunding an upgrade restores the earlier
            unrefunded package.
          </li>
          <li>
            Full terms:{" "}
            <Link
              to="/refund-policy"
              data-testid="plans-training-onetime-full-terms-link"
              className="underline hover:text-fg-primary"
            >
              /refund-policy
            </Link>
          </li>
        </ul>
      </div>

      {/* 30 / 60 / 90 package row */}
      <div
        data-testid="plans-training-packages"
        className="grid gap-4 md:grid-cols-3"
      >
        {pkg30 && (
          <PackageCard
            product={pkg30}
            amountCents={2999}
            owned={owns30 || owns60 || owns90}
            levelsLabel="Level 1"
            paymentsOpen={paymentsOpen}
            checkoutBusy={checkoutBusy}
            onBuy={() => onBuy("training_30d", null)}
            testid="plans-training-30d"
            signedIn={signedIn}
          />
        )}
        {pkg60 && (
          <PackageCard
            product={pkg60}
            amountCents={4999}
            owned={owns60 || owns90}
            levelsLabel="Levels 1 &amp; 2"
            paymentsOpen={paymentsOpen}
            checkoutBusy={checkoutBusy}
            onBuy={() => onBuy("training_60d", null)}
            testid="plans-training-60d"
            signedIn={signedIn}
          />
        )}
        {pkg90 && (
          <PackageCard
            product={pkg90}
            amountCents={6999}
            owned={owns90}
            levelsLabel="Levels 1, 2 &amp; 3"
            paymentsOpen={paymentsOpen}
            checkoutBusy={checkoutBusy}
            onBuy={() => onBuy("training_90d", null)}
            testid="plans-training-90d"
            signedIn={signedIn}
            featured
          />
        )}
      </div>

      {/* Round-28 (2026-10-08): package-label clarification note.
          The 30/60/90-day names describe a suggested learning
          schedule only; purchased months do not expire by elapsed
          days (full 12-month access ceiling applies per the refund
          policy). No price, offer, or access-rule change. */}
      <p
        data-testid="plans-training-packages-label-note"
        className="mt-4 text-xs text-fg-secondary"
      >
        The &ldquo;30-day&rdquo;, &ldquo;60-day&rdquo;, and
        &ldquo;90-day&rdquo; names above describe a suggested
        learning schedule. Months you have unlocked stay accessible
        for the full 12-month access period after purchase &mdash;
        lessons never lock because days elapsed.
      </p>

      {/* Upgrade credits — contextual, only shown when the predecessor
          grant is held by this user. Keeps the UI calm; the paywall
          itself is still the authoritative gate. */}
      {(owns30 || owns60) && paymentsOpen && (
        <div
          data-testid="plans-training-upgrades"
          className="mt-6 grid gap-4 md:grid-cols-2"
        >
          {owns30 && !owns60 && !owns90 && up3060 && (
            <UpgradeCard
              product={up3060}
              amountCents={2000}
              fromLabel="30-day package"
              toLabel="60-day package"
              checkoutBusy={checkoutBusy}
              onBuy={() => onBuy("training_30_to_60_upgrade", null)}
              testid="plans-training-upgrade-30-60"
            />
          )}
          {owns60 && !owns90 && up6090 && (
            <UpgradeCard
              product={up6090}
              amountCents={2000}
              fromLabel="60-day package"
              toLabel="90-day package"
              checkoutBusy={checkoutBusy}
              onBuy={() => onBuy("training_60_to_90_upgrade", null)}
              testid="plans-training-upgrade-60-90"
            />
          )}
        </div>
      )}
    </section>
  );
}


function PackageCard({ product, amountCents, owned, levelsLabel,
                       paymentsOpen, checkoutBusy, onBuy, testid,
                       signedIn, featured }) {
  const slug = product.slug;
  return (
    <div
      data-testid={testid}
      data-slug={slug}
      className={
        "relative rounded-lg border p-5 flex flex-col " +
        (featured
          ? "border-accent-warm/40 bg-accent-warm/5"
          : "border-border-subtle bg-bg-surface")
      }
    >
      <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
        One-time &middot; No recurring charge
      </p>
      <h3
        data-testid={`${testid}-name`}
        className="mt-1 text-lg font-heading font-medium text-fg-primary"
      >
        {product.name}
      </h3>
      <p
        data-testid={`${testid}-price`}
        className="mt-2 text-2xl font-heading text-fg-primary"
      >
        {formatUSD(amountCents)}
        <span className="text-xs text-fg-secondary ml-2">one-time</span>
      </p>
      <p
        data-testid={`${testid}-levels`}
        className="mt-2 text-xs uppercase tracking-widest text-progress-teal"
        dangerouslySetInnerHTML={{ __html: levelsLabel }}
      />
      <p
        data-testid={`${testid}-description`}
        className="mt-3 text-sm text-fg-secondary leading-relaxed flex-1"
      >
        {product.description}
      </p>
      <div className="mt-4">
        {owned ? (
          <p
            data-testid={`${testid}-owned`}
            className="text-[12px] uppercase tracking-widest text-progress-teal flex items-center gap-1"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Currently active on your account
          </p>
        ) : paymentsOpen ? (
          <Button
            data-testid={`${testid}-buy-btn`}
            onClick={onBuy}
            disabled={checkoutBusy != null}
            className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            {checkoutBusy === slug ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting&hellip;</>
            ) : (
              <>{signedIn ? "Buy" : "Create account & buy"} &mdash; {formatUSD(amountCents)}</>
            )}
          </Button>
        ) : (
          <p className="text-xs italic text-fg-muted">
            Purchasing not open yet.
          </p>
        )}
      </div>
    </div>
  );
}


function UpgradeCard({ product, amountCents, fromLabel, toLabel,
                       checkoutBusy, onBuy, testid }) {
  const slug = product.slug;
  return (
    <div
      data-testid={testid}
      data-slug={slug}
      className="rounded-lg border border-progress-teal/40 bg-progress-teal/5 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-progress-teal">
            Upgrade credit &middot; One-time
          </p>
          <h4
            data-testid={`${testid}-name`}
            className="mt-1 text-base font-heading font-medium text-fg-primary"
          >
            Upgrade from {fromLabel} to {toLabel}
          </h4>
          <p
            data-testid={`${testid}-description`}
            className="mt-1 text-xs text-fg-secondary"
          >
            Exact $20.00 upgrade &mdash; adds the next level on top of your
            earlier package. If the upgrade is later refunded, this upgrade's
            access is removed and your earlier package stays active.
          </p>
        </div>
        <Button
          data-testid={`${testid}-buy-btn`}
          onClick={onBuy}
          disabled={checkoutBusy != null}
          className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
        >
          {checkoutBusy === slug ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting&hellip;</>
          ) : (
            <>Upgrade &mdash; {formatUSD(amountCents)}</>
          )}
        </Button>
      </div>
    </div>
  );
}


function MembershipCard({ product, label, price, unit, alreadyOwned,
                           paymentsOpen, checkoutBusy, onBuy, testid,
                           signedIn }) {
  const slug = product.slug;
  return (
    <div
      data-testid={testid}
      data-slug={slug}
      className="rounded-lg border border-border-subtle bg-bg-surface p-5 flex flex-col"
    >
      <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
        {label} &middot; Recurring
      </p>
      <h3
        data-testid={`${testid}-name`}
        className="mt-1 text-lg font-heading font-medium text-fg-primary"
      >
        {product.name}
      </h3>
      <p
        data-testid={`${testid}-price`}
        className="mt-2 text-2xl font-heading text-fg-primary"
      >
        {price}
        <span className="text-xs text-fg-secondary ml-2">{unit}</span>
      </p>
      <p
        data-testid={`${testid}-description`}
        className="mt-3 text-sm text-fg-secondary leading-relaxed flex-1"
      >
        {product.description}
      </p>
      {product.renewal_terms && (
        <p
          data-testid={`${testid}-renewal-terms`}
          className="mt-3 text-xs text-fg-muted italic"
        >
          {product.renewal_terms}
        </p>
      )}
      <div className="mt-4">
        {alreadyOwned ? (
          <p
            data-testid={`${testid}-owned`}
            className="text-[12px] uppercase tracking-widest text-progress-teal flex items-center gap-1"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Currently active on your account
          </p>
        ) : paymentsOpen ? (
          <Button
            data-testid={`${testid}-buy-btn`}
            onClick={onBuy}
            disabled={checkoutBusy != null}
            className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            {checkoutBusy === slug ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting&hellip;</>
            ) : (
              <>{signedIn ? "Subscribe" : "Create account & subscribe"} &mdash; {price}{unit}</>
            )}
          </Button>
        ) : (
          <p className="text-xs italic text-fg-muted">
            Purchasing not open yet.
          </p>
        )}
      </div>
    </div>
  );
}


function AnnualMembershipCard({ product, phases, alreadyOwned,
                                 paymentsOpen, checkoutBusy, onBuy,
                                 testid, signedIn }) {
  const slug = product.slug;
  const phase0 = phases[0] || { amount_cents: 14999, unit: "year" };
  const phase1 = phases[1] || { amount_cents: 16999, unit: "year" };
  const modules = Array.isArray(product.training_modules)
    ? product.training_modules
    : [];
  return (
    <div
      data-testid={testid}
      data-slug={slug}
      className="rounded-xl border-2 border-accent-warm/70 bg-accent-warm/5 p-6 sm:p-8 flex flex-col shadow-lg shadow-accent-warm/5"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p
          data-testid={`${testid}-eyebrow`}
          className="text-[11px] uppercase tracking-widest text-progress-teal"
        >
          Recommended &middot; Full 12-month program
        </p>
        <Badge
          data-testid={`${testid}-badge`}
          className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
        >
          {product.best_value_badge || "Best annual value"}
        </Badge>
      </div>
      {/* Round-15 promotional headline (owner-exact wording). No
          cancellation / renewal language in the headline. */}
      <h3
        data-testid={`${testid}-headline`}
        className="mt-3 text-2xl sm:text-3xl font-heading font-medium text-fg-primary leading-tight"
      >
        Get all 12 months of AI training.
      </h3>
      <p
        data-testid={`${testid}-price-line`}
        className="mt-2 text-xl sm:text-2xl font-heading text-fg-primary"
      >
        <span className="text-accent-warm">
          {formatUSD(phase0.amount_cents)}
        </span>{" "}
        for your first year.
      </p>
      {/* Round-26 (owner-approved exact wording): promotional lines
          below the price. The marketing paragraph deliberately omits
          cancellation phrasing per the Round-26 owner directive; the
          formal cancellation disclosure is preserved on /refund-policy
          and on the Settings subscription block. */}
      <p
        data-testid={`${testid}-program-note`}
        className="mt-3 text-base text-fg-primary leading-relaxed"
      >
        <strong>Access every month immediately. Learn in any order.</strong>
      </p>
      <p
        data-testid={`${testid}-renewal-line`}
        className="mt-2 text-sm text-fg-secondary"
      >
        Renews automatically at{" "}
        <strong className="text-fg-primary">
          {formatUSD(phase1.amount_cents)}
        </strong>{" "}
        per year.
      </p>
      <p
        data-testid={`${testid}-name`}
        className="mt-4 text-xs uppercase tracking-widest text-fg-muted"
      >
        {product.name}
      </p>
      {modules.length > 0 && (
        <div
          data-testid={`${testid}-modules`}
          className="mt-5 rounded-md border border-border-subtle bg-bg-surface/60 p-4"
        >
          <p className="text-[11px] uppercase tracking-widest text-fg-secondary mb-3">
            What you get across the 12 months
          </p>
          <ol className="grid gap-y-1.5 gap-x-6 sm:grid-cols-2 list-none">
            {modules.map((m) => (
              <li
                key={m.month}
                data-testid={`${testid}-module-${m.month}`}
                className="flex items-baseline gap-2 text-sm text-fg-primary leading-snug"
              >
                <span
                  data-testid={`${testid}-module-${m.month}-number`}
                  className="flex-shrink-0 text-[11px] font-mono text-fg-muted w-10"
                >
                  Mo {m.month}
                </span>
                <span
                  data-testid={`${testid}-module-${m.month}-title`}
                  className="min-w-0"
                >
                  {m.title}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
      <p
        data-testid={`${testid}-arithmetic-breakdown`}
        className="mt-4 text-xs text-fg-secondary"
      >
        {formatUSD(phase0.amount_cents)} today billed as a single
        annual payment &mdash; about $12.50 a month equivalent across
        the first year.
      </p>
      <p
        data-testid={`${testid}-description`}
        className="mt-3 text-sm text-fg-secondary leading-relaxed flex-1"
      >
        {product.description}
      </p>
      {product.renewal_terms && (
        <p
          data-testid={`${testid}-renewal-terms`}
          className="mt-4 text-xs text-fg-muted italic leading-relaxed"
        >
          {product.renewal_terms}
        </p>
      )}
      <p
        data-testid={`${testid}-refund-link`}
        className="mt-3 text-xs text-fg-muted"
      >
        Full renewal and cancellation terms:{" "}
        <Link
          to="/refund-policy"
          data-testid={`${testid}-refund-link-anchor`}
          className="underline hover:text-fg-primary"
        >
          /refund-policy
        </Link>
      </p>
      <div className="mt-5">
        {alreadyOwned ? (
          <p
            data-testid={`${testid}-owned`}
            className="text-[12px] uppercase tracking-widest text-progress-teal flex items-center gap-1"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Currently active on your account
          </p>
        ) : paymentsOpen ? (
          <Button
            data-testid={`${testid}-buy-btn`}
            onClick={onBuy}
            disabled={checkoutBusy != null}
            className="w-full h-12 text-base bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            {checkoutBusy === slug ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting&hellip;</>
            ) : (
              <>{signedIn ? "Join the 12-month program" : "Create account & join"} &mdash; {formatUSD(phase0.amount_cents)}</>
            )}
          </Button>
        ) : (
          <p className="text-xs italic text-fg-muted">
            Purchasing not open yet.
          </p>
        )}
      </div>
    </div>
  );
}
